import React, {useEffect, useRef, useState} from 'react'
import debounce from 'lodash/debounce'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import { useTranslation } from "react-i18next"
import cx from 'classnames'

import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { LocationOn as LocationOnIcon } from '@vedmarket/ui-kit';

import {useLazyGetLocationByIdQuery, useLazyGetLocationsQuery} from "../../../../../../../api/locations";
import { COLORS } from "../../../../../../../config/constants";
import LocationFlag from 'components/common/location-flag';
import { getFlag } from '../../helpers';
import { LocalShipping, LocationCity, LocationSearching, Flight, DirectionsBoat, Train } from "@vedmarket/ui-kit";
import {DELIVERY_FORM, DeliveryFormFields} from "../../index";
import {useDemoDeliveryLocation} from 'components/cabinet/demo/steps/delivery/use-delivery';
import '../../../invoices-and-products/components/invoice/components/invoiceDetails/components/invoiceFields/locationInput/locationInput.scss';

const locationIconStyle = {
  color: COLORS.LIGHT_ACTION,
  fill: 'currentColor',
  width: '24px',
  height: '24px',
};

type OptionData = {
    label: string
}

type Props = {
    fieldName: DELIVERY_FORM
    label: string
    placeholder: string
    demoValue?: Pick<LocationItem, 'id' | 'name'>
    isBigInput?: boolean
    changeCityFrom?(id?: string): void
    changeCityTo?(id?: string): void
    defaultValue?: LocationItemDefaultCity
}

enum LOCATION_TYPE {
    PORT = 'port',
    CITY = 'city',
    RAIL_TERMINAL = 'rail terminal',
    AIRPORT = 'airport',
    ROAD_TERMINAL = 'road terminal',
    POSTAL_OFFICE = 'postal exchange office',
    MULTIMODAL_FUNCTION = 'multimodal functions (icds, etc.)',
    FIXED_TRANSPORT_FUNCTIONS = 'fixed transport functions (e.g. oil platform)',
    INLAND_PORT = 'inland port',
    BORDER_CROSSING = 'border crossing',
}

type LocationItemDefaultCity = LocationItem & OptionData;

const LocationInput = ({
  fieldName,
  label,
  placeholder,
  demoValue,
  isBigInput,
  changeCityFrom,
  changeCityTo,
  defaultValue,
}: Props) => {

  const { setValue, control, getValues, formState: { errors } } = useFormContext<DeliveryFormFields>()

  const [ hasValue, setHasValue ] = useState<boolean>(defaultValue ? true : Boolean(getValues()[fieldName]));

  const [ selectedLocation, setSelectedLocation ] = React.useState<LocationItemDefaultCity | null>(defaultValue || null);

  const [ userHasStartedTyping, setUserHasStartedTyping ] = useState(false);

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setSelectedLocation(defaultValue ? defaultValue : null);
    setHasValue(Boolean(defaultValue || getValues()[fieldName]));
  }, [ defaultValue ])

  const [ locationRequest, { data: dataLocations, isLoading: isLoadingCities, isSuccess: isSuccessCities } ] = useLazyGetLocationsQuery()
  const shouldShowLocations = userHasStartedTyping && isSuccessCities;
  const [ locationByIdRequest ] = useLazyGetLocationByIdQuery()

  const debouncedRequest = useRef(debounce(locationRequest, 1000)).current

  const getLocationsWithoutCities = () => (dataLocations || [])
    .filter(item => item.type !== LOCATION_TYPE.CITY)
    .map(item => ({
      ...item,
      group: JSON.stringify({
        groupName: item.city.name,
        groupCode: item.city.code,
        groupCountryCode: item.city.country.iso2 || item.city.country.code,
        groupCountryName: item.city.country.name,
        groupId: item.city.id,
      }),
      label: `${item.name}, ${item.country.name} ${item.city.name}`
    }));

  const getLocationsCitiesOnly = () => {

    const groupedCityIds = new Set(
      (dataLocations || [])
        .filter(item => item.type !== LOCATION_TYPE.CITY && item.city?.id)
        .map(item => item.city.id)
    );

    return (dataLocations || [])
      .filter(item => item.type === LOCATION_TYPE.CITY && !groupedCityIds.has(item.id))
      .map(item => ({
        ...item,
        group: JSON.stringify({
          groupName: item.name,
          groupCode: item.code,
          groupCountryCode: item.country.iso2 || item.country.code,
          groupCountryName: item.country.name,
          groupId: item.id,
          type: LOCATION_TYPE.CITY,
        }),
        label: `${item.name}, ${item.country.name}`
      }));
  }

  const locationsWithoutCities = getLocationsWithoutCities();
  const locationsWithCitiesOnly = getLocationsCitiesOnly();

  const locations = [ ...locationsWithoutCities, ...locationsWithCitiesOnly ]

  const { t } = useTranslation()

  const fieldValue = useWatch({ control, name: fieldName });

  useEffect(() => {
    setHasValue(Boolean(fieldValue));
  }, [ fieldValue ]);

  useEffect(() => () => debouncedRequest.cancel(), [ debouncedRequest ]);

  const cityInputStart = <InputAdornment position='start' sx={{
    marginLeft: '-3px',
    marginRight: '1px',
    '& .MuiTypography-root': {
      color: COLORS.BLACK_DARK,
    },
  }}>
    <Typography variant='subtitle1' align='center' className='searchForm__adornment' sx={{ fontWeight: 900, fontSize: '20px', lineHeight: '30px', color: COLORS.BLACK_DARK, marginRight: 0 }}>
      {fieldName === DELIVERY_FORM.LOCATION_FROM ? 'A' : 'B'}
    </Typography>
  </InputAdornment>


  const getIcon = (type: string) => {
    switch (type) {
      case LOCATION_TYPE.AIRPORT:
        return <Flight className='location-option__sign'/>
      case LOCATION_TYPE.RAIL_TERMINAL:
        return <Train className='location-option__sign'/>
      case LOCATION_TYPE.PORT:
        return <DirectionsBoat className='location-option__sign'/>
      case LOCATION_TYPE.ROAD_TERMINAL:
        return <LocalShipping className='location-option__sign'/>
      case LOCATION_TYPE.CITY:
        return <LocationCity className='location-option__sign'/>
      default:
        return null;
    }
  }

  const handleGroupClick = async (id: string) => {

    setValue(fieldName, id, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    inputRef.current?.blur();

    const cityId = id.split("city_")[1];

    await locationByIdRequest(cityId).then( async (res) => {
      const currentCity = res.data;
      if (!currentCity) return;
      // @ts-ignore
      setSelectedLocation({...currentCity, label: `${currentCity?.name}, ${currentCity?.country?.name} ${currentCity?.city?.name}`});

      if (fieldName === DELIVERY_FORM.LOCATION_FROM) {
        changeCityFrom && changeCityFrom(id)
      }
      if (fieldName === DELIVERY_FORM.LOCATION_TO) {
        changeCityTo && changeCityTo(id)
      }

    });

  }

  const handleSetAutocomplete = async (value: any, reason: string) => {

    if (reason === 'clear') {
      setUserHasStartedTyping(false);
    }

    if (!value) {
      setSelectedLocation(null);
      setValue(fieldName, '', { shouldDirty: true, shouldValidate: true });
      changeCityFrom?.(undefined);
      changeCityTo?.(undefined);
      return;
    }

    setSelectedLocation(value);

    setValue(fieldName, reason !== 'clear' ? value?.id : undefined);

    if (fieldName === DELIVERY_FORM.LOCATION_FROM) {
      changeCityFrom && changeCityFrom(reason !== 'clear' ? value.id : undefined)
    }
    if (fieldName === DELIVERY_FORM.LOCATION_TO) {
      changeCityTo && changeCityTo(reason !== 'clear' ? value.id : undefined)
    }

  }

  const demoPlayback = useDemoDeliveryLocation({
    demoValue,
    fieldName,
    fromFieldName: DELIVERY_FORM.LOCATION_FROM,
    getFromValue: () => getValues()[DELIVERY_FORM.LOCATION_FROM],
    inputRef,
    locations,
    locationRequest,
    onChoose: location => handleSetAutocomplete(location, 'selectOption'),
    onSearchComplete: () => setUserHasStartedTyping(true),
  })

  return (
    <div
      id={fieldName}
      data-demo-delivery-location={demoPlayback.fieldName}
      className={cx({
        'delivery-location': true,
        'searchForm__city': true,
        'searchForm__city_big': isBigInput,
      })}
    >
      <Controller
        // @ts-ignore
        name={fieldName}
        control={control}
        rules={{ required: t('common__text__isRequired') }}
        render={() => (
          <Autocomplete
            options={locations}
            loading={isLoadingCities}
            noOptionsText={t('calc__text__main__noOptionsFound') }
            defaultValue={defaultValue}
            value={selectedLocation}
            {...demoPlayback.autocompleteProps}
            renderOption={(props, option) => (
              <li
                {...props}
                data-demo-delivery-location-option={demoPlayback.getOptionFieldName(option.id)}
                key={option.id}
                className='location-option'
              >
                <span className='location-option__image'>{getIcon(option.type)}</span>
                <Typography variant='body2' sx={{
                  fontSize: '14px',
                  lineHeight: '21px',
                  letterSpacing: 0,
                  color: COLORS.BLACK_DARK
                }}>
                  <span className='location-option__name'>
                    {option.name}
                  </span>
                  <span className='location-option__id'>
                    {option.id}
                  </span>
                </Typography>
              </li>
            )}
            // @ts-ignore
            groupBy={(option) => option.group}
            renderGroup={(params) => {
              if (!params.group) return null;
              const meta = JSON.parse(params.group);
              return (
                <li key={params.key} className='location-group'>
                  <div
                    className='location-group__main'
                    data-demo-delivery-location-option={demoPlayback.getOptionFieldName(meta.groupId)}
                    onClick={() => handleGroupClick(meta.groupId)}
                  >
                    <div className='location-group__flag'>
                      <LocationFlag code={meta.groupCountryCode} />
                    </div>
                    <div className='location-group__info'>
                      <Typography variant='body1' sx={{
                        lineHeight: '24px',
                        letterSpacing: 0,
                        color: COLORS.BLACK_DARK,
                      }}>
                        <span className='location-group__name'>
                          {meta.groupName}
                        </span>
                        <span className='location-group__code'>
                          {meta.groupCode}
                        </span>
                      </Typography>
                      <Typography variant='caption' sx={{
                        fontSize: '12px',
                        lineHeight: '12px',
                        letterSpacing: 0,
                        color: COLORS.LIGHT_TEXT_DISABLED,
                      }}>
                        {meta.groupCountryName}
                      </Typography>
                    </div>
                  </div>
                  {meta.type !== LOCATION_TYPE.CITY && params.children}
                </li>
              )
            }}
            filterOptions={(options) => options}
            PaperComponent={({ children }) => {
              if (!shouldShowLocations) return null;

              return (
                <Paper elevation={8} sx={{
                  borderRadius: 0,
                  marginTop: '8px',
                  '& [role="option"].Mui-focused': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '& .MuiAutocomplete-listbox': {
                    padding: '8px 0',
                    maxHeight: 'calc(var(--calculator-popup-height, 100vh) - 8px)',
                  },
                }}>
                  {children}
                </Paper>
              )
            }}
            renderValue={(value, getItemProps) => {

              if (!value) return null;

              const icon = getIcon(value.type);

              const name = value?.city?.name || value?.name;
              const countryCode = value?.country?.iso2 || value?.country?.code;
              const { onDelete, ...itemProps } = getItemProps();

              return <Typography component='div' variant='body1' {...itemProps} className='location-value' sx={{
                lineHeight: '24px',
                letterSpacing: 0,
              }}>
                <span className='delivery-location__flag'>
                  {getFlag({ code: countryCode, width: '32px', marginRight: '0' })}
                </span>
                <span className='location-value__city delivery-location__city'>
                  {name && <span className='location-value__name'>{name} ({value.country.name})</span>}
                  <span className='location-value__code'>{value.code}</span>
                </span>
                {value?.city?.name && icon && (
                  <span className='location-value__image'>
                    {icon}
                  </span>
                )}
                {value?.city?.name && <span className='location-value__detail'>
                  <span className='location-value__location-name'>
                    {value.name}
                  </span>
                  <span className='location-value__id'>
                    {value.id}
                  </span>
                </span>}
              </Typography>;
            }}
            // @ts-ignore
            onChange={(_, value, reason ) => handleSetAutocomplete(value, reason)}
            isOptionEqualToValue={(o: LocationItem, v: LocationItem) => o.id === v.id}
            loadingText={t('common__message__loadingText')}
            renderInput={(params) => {
              const placeholderValue = hasValue || selectedLocation ? '' : placeholder;

              return <TextField
                {...params}
                error={Boolean(errors[fieldName])}
                placeholder={placeholderValue}
                helperText={errors[fieldName]?.message}
                inputRef={inputRef}
                variant='outlined'
                label={label}
                inputProps={{
                  ...params.inputProps,
                  placeholder: placeholderValue,
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <>
                    {params.inputProps.value && <InputAdornment position='start' sx={{ marginRight: 0 }}>
                      <LocationOnIcon style={locationIconStyle} />
                    </InputAdornment>}
                    {!params.inputProps.value && <>
                      {cityInputStart}
                      {params.InputProps.startAdornment}
                      {!selectedLocation && getFlag({ code: fieldName === DELIVERY_FORM.LOCATION_FROM ? 'CN' : 'RU', width: '32px', marginRight: '0' })}
                    </>}</>,
                  endAdornment: (
                    <InputAdornment position='end' sx={{
                      right: '12px',
                      position: 'absolute',
                      margin: 0,
                    }}>
                      {params.inputProps.value && <LocationSearching style={locationIconStyle} />}
                    </InputAdornment>
                  ),
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && !params.inputProps.value) {
                    handleSetAutocomplete(null, 'clear');
                  }
                }}
                onChange={ async (e) => {
                  params.inputProps.onChange?.(
                      e as React.ChangeEvent<HTMLInputElement>
                  );

                  const query = (e.target as HTMLInputElement | HTMLTextAreaElement).value;
                  const fromValue = getValues()[DELIVERY_FORM.LOCATION_FROM];
                  if (query.length >= 3) {
                    debouncedRequest({q: query, include_extra: true, deps_id: fieldName === DELIVERY_FORM.LOCATION_TO ? fromValue : undefined});
                    setUserHasStartedTyping(true);
                  } else {
                    debouncedRequest.cancel();
                    setUserHasStartedTyping(false);
                  }
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
                    height: '48px',
                    minHeight: '48px',
                    padding: '12px 44px 12px 12px',
                    gap: '8px',
                  },
                  '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
                    padding: 0,
                    lineHeight: '24px',
                    letterSpacing: 0,
                    paddingLeft: params.inputProps.value ? '4px' : '2px',
                    transform: params.inputProps.value ? 'none' : 'translateY(-1px)',
                  },
                  '& .MuiInputBase-input:placeholder-shown': {
                    minWidth: selectedLocation ? '1px' : '30px',
                    width: selectedLocation ? '1px' : 'auto',
                    position: selectedLocation ? 'absolute' : 'static',
                    opacity: selectedLocation ? 0 : 1,
                    left: '92px',
                  },
                  '& .MuiInputBase-input:not(:placeholder-shown)': {
                    minWidth: '30px',
                    width: '30px',
                    opacity: 1,
                    zIndex: 0,
                  },
                }}
              />
            }}
          />)}
      />
    </div>
  )
}

export default LocationInput
